import React, { useId, useMemo } from 'react'
import styled from 'styled-components'

import { useHapticFeedback } from '@blade/ui-kit'

import { useValueContext, ValueContext } from '../value-context'

import type { IconProps } from '../icons'
import type { ValueContextValue } from '../value-context'
import type { ChildrenProps, ClassNameProps } from '@root/shared/react'

type ContextValue<T> = ValueContextValue<{
  getId: (value: T) => string
  value: T
  onChange: (value: T) => void
  disabled?: boolean
}>

type SegmentControlProps<T> = {
  value: T
  onChange?: (value: T) => void
  disabled?: boolean
} & ChildrenProps &
  ClassNameProps &
  (T extends string
    ? {
        getId?: (value: string) => string
      }
    : {
        getId: (value: T) => string
      })

const SegmentControl_ = <T = string,>({
  value,
  onChange,
  children,
  className,
  getId,
  disabled,
}: SegmentControlProps<T>) => {
  const layoutId = useId()
  const haptics = useHapticFeedback()
  const ctx = useMemo(
    (): ContextValue<T> => ({
      value: value as T & string,
      getId: (getId ?? ((x: T) => x)) as (value: T) => string,
      onChange: (value) => {
        if (onChange) {
          haptics.impactOccurred('light')
          onChange(value as T)
        }
      },
      layoutId: layoutId,
    }),
    [haptics, layoutId, onChange, value, getId],
  )

  return (
    <Root className={className} aria-disabled={disabled}>
      <ValueContext.Provider value={ctx}>{children}</ValueContext.Provider>
    </Root>
  )
}

const Root = styled.div`
  padding: 3px;

  display: inline-flex;
  align-items: center;
  overflow: hidden;

  height: 36px;
  border-radius: 20px;
  background: var(--appearanceA-4);
  gap: 1px;
`

type ItemProps<T> = {
  value: T
  Icon?: React.FC<IconProps>
  disabled?: boolean
} & React.PropsWithChildren &
  ClassNameProps

const Item = <T,>({
  children,
  value,
  Icon,
  className,
  disabled,
}: ItemProps<T>) => {
  const ctx = useValueContext() as ContextValue<T>
  const isActive = ctx.getId(ctx.value) === ctx.getId(value)

  const renderIcon = () => {
    if (!Icon) {
      return undefined
    }

    return <Icon size={16} aria-checked={isActive} />
  }

  return (
    <ItemRoot
      className={className}
      $active={isActive}
      disabled={disabled}
      aria-checked={isActive}
      type="button"
      onClick={() => {
        ctx.onChange(value)
      }}
    >
      {renderIcon()}
      <Items>{children}</Items>
    </ItemRoot>
  )
}

const ItemRoot = styled.button<{ $active: boolean }>`
  border-radius: 17px;
  flex: 1 1 0;
  transition: all 0.15s linear;
  position: relative;
  white-space: nowrap;

  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  background: var(--backgroundA-2);
  padding: 5px 16px;
  gap: 4px;
  border: 1px solid transparent;
  height: 30px;

  font-size: 12px;
  font-weight: 600;
  line-height: 20px;

  color: var(--text-11);

  &[aria-checked='true'] {
    color: var(--text-12);
    background: var(--primary-2);
    border-color: var(--primary-9);
  }

  & svg {
    transition: all 0.15s linear;
  }

  & > span {
    position: relative;
  }

  &:hover,
  &:focus-visible {
    color: var(--text-12);
  }

  ${Root}[aria-disabled='true'] & {
    color: var(--text-6);
    cursor: not-allowed;

    &[aria-checked='true'],
    &:hover,
    &:focus-visible {
      color: var(--text-6);
      background: rgba(255, 255, 255, 0.05);
      border-color: transparent;
    }
  }
`

const Items = styled.div`
  will-change: contents, transform;
`

type Compound = {
  Item: typeof Item
} & typeof SegmentControl_

/**
 * @deprecated Use SegmentControl from @blade/ui-kit instead
 */
const SegmentControl = SegmentControl_ as Compound
SegmentControl.Item = Item

export { SegmentControl }
